// Import redux states.
import { 
    AppState,
    MinCostPerSquareFootVersionsState 
} from '@app/redux/states';
 // Import api models.
 import {
    MinCostPerSquareFootVersionFriendlyViewSummary,
    MinCostPerSquareFootVersionResponse
} from '@app/models/api';
 // Impor reselect.
import {
    createSelector
} from 'reselect';
// Import selectors.
import {
    getAdministratorState,
} from '@app/redux/selectors/base';
import {
    VersionProps
} from '@app/props';

const getMinCostPerSquareFootVersionsState = (state: AppState): MinCostPerSquareFootVersionsState | undefined => {
    return getAdministratorState(state)?.minCostPerSquareFootVersionsState ?? undefined;
}

const getVersionIdFromProps = (state: AppState, props: VersionProps): number => {
    return props.versionId;
}

// Get Min Cost Per Square Foot Versions Selector.
const makeGetMinCostPerSquareFootVersionsSelector = () => createSelector(
    [getMinCostPerSquareFootVersionsState],
    (minCostPerSquareFootVersionState: MinCostPerSquareFootVersionsState | undefined): MinCostPerSquareFootVersionResponse[] => {
        return minCostPerSquareFootVersionState?.minCostPerSquareFootVersions ?? [];
    }
);

// Get Min Cost Per Square Foot Version Friendly View Summaries Selector.
const makeGetMinCostPerSquareFootVersionFriendlyViewSummariesSelector = () => createSelector(
    [makeGetMinCostPerSquareFootVersionsSelector()],
    (minCostPerSquareFootVersions: MinCostPerSquareFootVersionResponse[]): MinCostPerSquareFootVersionFriendlyViewSummary[] => {
        return minCostPerSquareFootVersions?.map((minCostPerSquareFootVersion: MinCostPerSquareFootVersionResponse) => {
            return new MinCostPerSquareFootVersionFriendlyViewSummary({
                id: minCostPerSquareFootVersion.id ?? 0,
                status: minCostPerSquareFootVersion.completedDate !== null
                    ? 'Completed'
                    : 'In Progress',
                description: minCostPerSquareFootVersion.description ?? '',
            });
        });
    }
);

// Get In Progress Version Id Selector.
const makeGetInProgressVersionIdSelector = () => createSelector(
    [makeGetMinCostPerSquareFootVersionsSelector()],
    (minCostPerSquareFootVersions: MinCostPerSquareFootVersionResponse[]): number | undefined => {
        return minCostPerSquareFootVersions.find(
            (minCostPerSquareFootVersion: MinCostPerSquareFootVersionResponse) =>
                minCostPerSquareFootVersion.completedDate === null
        )?.id ?? undefined;
    }
);

// Get Selected Min Cost Per Square Foot Version Id Selector.
const makeGetSelectedMinCostPerSquareFootVersionIdSelector = () => createSelector(
    [getMinCostPerSquareFootVersionsState],
    (minCostPerSquareFootVersionState: MinCostPerSquareFootVersionsState | undefined): number | undefined => {
        return minCostPerSquareFootVersionState?.selectedMinCostPerSquareFootVersionId ?? undefined
    }
);

// Get Selected Min Cost Per Square Foot Version Selector.
const makeGetSelectedMinCostPerSquareFootVersionSelector = () => createSelector(
    [
        makeGetSelectedMinCostPerSquareFootVersionIdSelector(),
        makeGetMinCostPerSquareFootVersionsSelector(),
    ],
    (
        selectedMinCostPerSquareFootVersionId: number | undefined,
        minCostPerSquareFootVersions: MinCostPerSquareFootVersionResponse[]
    ): MinCostPerSquareFootVersionResponse | undefined => {
        return selectedMinCostPerSquareFootVersionId !== undefined
            ? minCostPerSquareFootVersions.find((minCostPerSquareFootVersion: MinCostPerSquareFootVersionResponse) => minCostPerSquareFootVersion.id === selectedMinCostPerSquareFootVersionId)
            : undefined;
    }
);

// Is Version Id In Progress Selector.
const makeIsVersionIdInProgressSelector = () => createSelector(
    [getVersionIdFromProps, makeGetInProgressVersionIdSelector()],
    (versionId: number, inProgressVersionId: number | undefined): boolean =>
        inProgressVersionId !== undefined && versionId === inProgressVersionId
);

// Are Min Cost Per Square Foot Versions Loaded Selector.
const makeAreMinCostPerSquareFootVersionsLoadedSelector = () => createSelector(
    [getMinCostPerSquareFootVersionsState],
    (minCostPerSquareFootVersionState: MinCostPerSquareFootVersionsState | undefined): boolean => {
        return minCostPerSquareFootVersionState?.areMinCostPerSquareFootVersionsLoaded ?? false;
    }
);

// Are Any Min Cost Per Square Foot Versions In Progress Selector.
const makeAreAnyMinCostPerSquareFootVersionsInProgressSelector = () => createSelector(
    [makeGetMinCostPerSquareFootVersionFriendlyViewSummariesSelector()],
    (minCostPerSquareFootVersions: MinCostPerSquareFootVersionFriendlyViewSummary[]): boolean => {
        return minCostPerSquareFootVersions.some((minCostPerSquareFootVersion: MinCostPerSquareFootVersionFriendlyViewSummary) => minCostPerSquareFootVersion.status === 'In Progress');
    }
);

export {
    getMinCostPerSquareFootVersionsState,
}

export {
    makeGetMinCostPerSquareFootVersionsSelector,
    makeGetMinCostPerSquareFootVersionFriendlyViewSummariesSelector,
    makeGetInProgressVersionIdSelector,
    makeIsVersionIdInProgressSelector,
    makeAreMinCostPerSquareFootVersionsLoadedSelector,
    makeGetSelectedMinCostPerSquareFootVersionIdSelector,
    makeGetSelectedMinCostPerSquareFootVersionSelector,
    makeAreAnyMinCostPerSquareFootVersionsInProgressSelector,
}